import styled from '@emotion/styled';
import moment from 'moment';
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestionCircle } from '@fortawesome/pro-solid-svg-icons';
import { UserSubscriptionBillingCycleUsages } from '../../../../ReduxStorage/InstitutionUsage/types';
import { SubscriptionPlanType } from '../../../../ReduxStorage/SubscriptionPlans/constants';
import { UserSubscriptionMonthlyUsageData } from '../../../../ReduxStorage/UserSubscriptionPlans/constants';
import type {
  SubscriptionLinkedInstitutionsTableItem,
  UserSubscriptionBillingPeriod,
} from '../../../../ReduxStorage/UserSubscriptionPlans/selectors';
import { Button } from '../../../components/Marketing/Button';
import { InstitutionUsageTable } from '../../../components/SubscriptionsHelper/InstitutionUsageTable/component';
import { UsageOverTimeChart } from '../../../components/SubscriptionsHelper/UsageOverTimeChart/component';
import { UsageProgress } from '../../../components/SubscriptionsHelper/UsageProgress/component';
import { Tooltip } from '../../../Tooltip/Tooltip';
import { OL_COLOUR_NAVY_BABY } from '../../../components/Style/colours';

const Heading = styled('div')({
  fontSize: 18,
  borderBottom: '1px solid #CACACA',
  fontWeight: 600,
  lineHeight: '35px',
  marginBottom: 20,
});

const SubHeading = styled('div')({
  fontSize: 16,
  fontWeight: 600,
});

const CTAContainer = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  marginBottom: 20,
});

const LearnerUsageOverTimeContainer = styled('div')({
  padding: '14px 12px 34px 12px',
});

const LearnerUsageChartContainer = styled('div')({
  marginTop: 16,
});

const LearnerUsageByInstitutionContainer = styled('div')({
  marginTop: 40,
});

const TooltipOverlay = styled('div')({
  maxWidth: 300,
});

const QuestionCircleIcon = styled(FontAwesomeIcon)({
  fontSize: 14,
  color: OL_COLOUR_NAVY_BABY,
});

const StyledTooltip = styled(Tooltip)({
  marginLeft: 8,
});

export const UsageView = React.memo(
  ({
    planType,
    UsageLimitExceededBanner,
    monthlyUsages,
    billingCycleUsages,
    educatorSeats,
    educatorLimit,
    learnerLimit,
    portalLimit,
    isOwner,
    billingPeriods,
    subscriptionId,
    portalCount,
    learnersByInstitution,
    monthlyUsagesIsLoading,
    fetchMonthlyUsageData,
    changePlan,
  }: {
    planType: SubscriptionPlanType;
    UsageLimitExceededBanner: JSX.Element | null;
    monthlyUsages: UserSubscriptionMonthlyUsageData[];
    billingCycleUsages: UserSubscriptionBillingCycleUsages;
    educatorSeats: number;
    educatorLimit: number;
    learnerLimit: number;
    portalLimit: number | null;
    portalCount: number;
    isOwner: boolean;
    billingPeriods: Array<UserSubscriptionBillingPeriod>;
    learnersByInstitution: SubscriptionLinkedInstitutionsTableItem[];
    subscriptionId: string;
    monthlyUsagesIsLoading: boolean;
    fetchMonthlyUsageData: (
      subscriptionId: string,
      billingCycleStart: string,
      billingCycleEnd: string
    ) => void;
    changePlan?: () => void;
  }) => {
    return (
      <div>
        <Heading>Account usage</Heading>
        {UsageLimitExceededBanner}
        {billingCycleUsages.length > 0 && (
          <UsageProgress
            label="Learners for current period"
            currentPeriodLabel={`${moment(
              billingCycleUsages[0].billingCycleStart
            ).format('DD MMM YYYY')}`}
            usageResetLabel={`${moment(
              billingCycleUsages[0].billingCycleEnd
            ).format('DD MMM YYYY')}`}
            value={billingCycleUsages.map((usage) => usage.numLearners)}
            max={learnerLimit}
          />
        )}
        <UsageProgress
          label="Educator seat"
          value={[educatorSeats]}
          max={educatorLimit}
        />
        {planType === 'custom' && (
          <UsageProgress
            label="Portal limit"
            value={[portalCount]}
            max={portalLimit}
          />
        )}
        {isOwner && changePlan && (
          <CTAContainer>
            <Button
              variant="legacy-cta-pink"
              action={{
                kind: 'on_click',
                value: changePlan,
              }}
            >
              Change plan
            </Button>
          </CTAContainer>
        )}
        <LearnerUsageOverTimeContainer>
          <LearnerUsageChartContainer>
            <UsageOverTimeChart
              isLoading={monthlyUsagesIsLoading}
              title={
                <>
                  Number of learner enrolments{' '}
                  <StyledTooltip
                    placement="right"
                    overlay={
                      <TooltipOverlay>
                        This graph represents a monthly segmentation of all the
                        learners that have enrolled in a course within your
                        subscription.
                      </TooltipOverlay>
                    }
                  >
                    <QuestionCircleIcon icon={faQuestionCircle} />
                  </StyledTooltip>
                </>
              }
              data={monthlyUsages.map((usage) => {
                return {
                  name: usage.yearAndMonth,
                  amount: usage.numLearners,
                };
              })}
              selectOptions={billingPeriods.map((billingPeriod) => {
                return {
                  label: `${moment(billingPeriod.billingCycleStart).format(
                    'DD MMM YYYY'
                  )} - ${moment(billingPeriod.billingCycleEnd).format(
                    'DD MMM YYYY'
                  )}`,
                  value: `${billingPeriod.billingCycleStart}/${billingPeriod.billingCycleEnd}`,
                };
              })}
              onChange={(billingPeriod) => {
                const [
                  billingCycleStart,
                  billingCycleEnd,
                ] = billingPeriod.split('/');
                fetchMonthlyUsageData(
                  subscriptionId,
                  billingCycleStart,
                  billingCycleEnd
                );
              }}
            />
          </LearnerUsageChartContainer>
        </LearnerUsageOverTimeContainer>
        {planType === 'custom' && (
          <LearnerUsageByInstitutionContainer>
            <SubHeading>Active learners by institution</SubHeading>
            <InstitutionUsageTable institutions={learnersByInstitution} />
          </LearnerUsageByInstitutionContainer>
        )}
      </div>
    );
  }
);
